import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import NotifyMe from "../components/NotifyMe";
import { SectionHead, Reveal } from "../components/Motion";
import { FESTIVALS, OCCASIONS, RECIPIENTS, CATEGORIES, findFestival, findOccasion, findRecipient, findFestival as _f, label } from "../data/taxonomy";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/api";

export default function Shop({ mode }) {
  const { category, slug } = useParams();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [notifyProduct, setNotifyProduct] = useState(null);
  const { settings } = useStore();
  const q = searchParams.get("q") || "";

  useEffect(() => {
    setProducts(null);
    if (mode === "festivals" || mode === "occasions") return;
    let url = "/products";
    const params = new URLSearchParams();
    if (mode === "category" && category) params.set("category", category);
    if (mode === "festival" && slug) params.set("festival", slug);
    if (mode === "occasion" && slug) params.set("occasion", slug);
    if (mode === "recipient" && slug) params.set("recipient", slug);
    if (mode === "search" && q) params.set("q", q);
    if (mode === "offers") params.set("availability", "available");
    if ([...params].length) url += `?${params}`;
    api.get(url).then((r) => setProducts(r.data.products)).catch(() => setProducts([]));
  }, [mode, category, slug, q]);

  if (mode === "festivals" || mode === "occasions") {
    const items = mode === "festivals" ? FESTIVALS : OCCASIONS;
    const imgMap = (mode === "festivals" ? settings?.festival_images : settings?.occasion_images) || {};
    return (
      <div className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 min-h-screen">
        <SectionHead overline={mode === "festivals" ? "Shop by Festival" : "Shop by Occasion"}
          title={mode === "festivals" ? "Every Festival, Gilded" : "Every Occasion, Remembered"} testid={`${mode}-page-head`} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {items.map((it, i) => (
            <Reveal key={it.slug} delay={(i % 4) * 0.06}>
              <Link to={`/${mode === "festivals" ? "festival" : "occasion"}/${it.slug}`} data-testid={`${mode}-card-${it.slug}`}
                className="group relative block aspect-[4/5] overflow-hidden rounded-2xl card-surface gold-ring-hover hover:-translate-y-1.5">
                <img src={imgMap[it.slug] || it.img} alt={it.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <h3 className="font-serif-lux text-xl text-[#2E1A22]">{it.name}</h3>
                  {it.desc && <p className="text-xs text-[#857078] mt-1">{it.desc}</p>}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    );
  }

  const meta =
    mode === "category" ? CATEGORIES.find((c) => c.slug === category) :
    mode === "festival" ? findFestival(slug) :
    mode === "occasion" ? findOccasion(slug) :
    mode === "recipient" ? findRecipient(slug) : null;

  const title = mode === "search" ? `Results for "${q}"` :
    mode === "offers" ? "Offers & Available Now" :
    meta ? meta.name : mode === "festival" || mode === "occasion" || mode === "recipient" ? label(slug || "") : "All Gifts";

  return (
    <div className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 min-h-screen" data-testid="shop-page">
      <SectionHead
        overline={mode === "offers" ? "Available Now" : mode === "search" ? "Search" : "The Savyron Collection"}
        title={title}
        sub={mode === "offers" ? "Everything you can order today — secure prepaid checkout, estimated delivery in 2–4 business days." : meta?.desc}
        testid="shop-head" />
      {mode === "offers" && <OffersBanner />}
      {products === null ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-[#FFFFFF] animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20" data-testid="shop-empty">
          <p className="font-serif-lux text-2xl text-[#857078]">No gifts found here yet</p>
          <p className="text-sm text-[#857078] mt-2">This collection is being curated. Explore the full range meanwhile.</p>
          <Link to="/gifts" className="inline-block mt-6 text-xs tracking-[0.15em] uppercase font-semibold bg-[#7E1E3F] text-[#FFFFFF] rounded-full px-6 py-3" data-testid="shop-empty-cta">View All Gifts</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="shop-grid">
          {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onNotify={setNotifyProduct} />)}
        </div>
      )}
      <NotifyMe product={notifyProduct} onClose={() => setNotifyProduct(null)} />
    </div>
  );
}

function OffersBanner() {
  const { settings } = useStore();
  return (
    <Reveal className="mb-10">
      <div className="card-surface rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4" data-testid="offers-banner">
        <div>
          <p className="font-mono-lux text-[11px] uppercase tracking-[0.25em] text-[#7E1E3F]">New Customer Offer</p>
          <p className="font-serif-lux text-2xl text-[#2E1A22] mt-1">10% off your first order with code <span className="gold-text font-semibold">WELCOME10</span></p>
          <p className="text-sm text-[#857078] mt-1">Plus: 2 Sonata watches @ {inr(1499)} — the bundle applies automatically.</p>
        </div>
      </div>
    </Reveal>
  );
}
